import { module } from 'modujs';
import { mapRange, clamp } from '../utils/maths';
import gsap from 'gsap'

const TOUCH_BREAKPOINT = 999;

export default class extends module {
    constructor(m) {
        super(m);

        // Binding
        this.onResizeBind = this.onResize.bind(this);
        this.onBreakPointChangeBind = this.onBreakPointChange.bind(this);
        this.onUpdateBind = this.onUpdate.bind(this)
        this.onScrollBind = this.onScroll.bind(this)

        // UI
        this.$item = this.$('item');
        this.$container = this.el;
        this.$grid = this.$('grid')[0];
        this.$sticky = this.$('sticky')[0];

        // Data
        this.containerWidth = 0
        this.containerHeight = 0;
        this.breakpoint = window.matchMedia( `(min-width: ${TOUCH_BREAKPOINT}px)` );
        
        // Progress
        this.lerp = .3
        this.progress = 0
        this.smoothProgress = 0

        // Flags
        this.isRafPlaying = false
        this.isInview = false
        this.enableSticky = true
        this.isFirstInit = true

    }

    ///////////////
    // Lifecyle
    ///////////////
    init() {
        this.bindEvents();
        this.onBreakPointChange()
    }

    destroy() {
        this.unbindEvents();
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        window.addEventListener('resize', this.onResizeBind);
        document.addEventListener('scroll', this.onScrollBind);

        if ('addEventListener' in this.breakpoint) {
            this.breakpoint.addEventListener('change', this.onBreakPointChangeBind )
        } else if ('addListener' in this.breakpoint) {
            this.breakpoint.addListener(this.onBreakPointChangeBind )
        }
    }

    unbindEvents() {
        window.removeEventListener('resize', this.onResizeBind);
        document.removeEventListener('scroll', this.onScrollBind);

        if ('removeEventListener' in this.breakpoint) {
            this.breakpoint.addEventListener('change', this.onBreakPointChangeBind )
        } else if ('removeListener' in this.breakpoint) {
            this.breakpoint.addListener(this.onBreakPointChangeBind )
        }
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        if (window.innerWidth > TOUCH_BREAKPOINT) {
            this.computeContainerHeight()
            this.computeStickyTop()
        }
    }

    onBreakPointChange(callback) {
        if ( this.breakpoint.matches === true ) {
            if (this.isFirstInit) {
                this.computeContainerHeight()
                this.enableSticky && this.computeStickyTop()
                this.isFirstInit = false
            }
        } else if ( this.breakpoint.matches === false ) {
            window.requestAnimationFrame(() => {
                this.reset()
            })
        }
    }

    onInview(args) {
        this.isInview = (args.way === 'enter')
    }

    onScrollProgress(progress) {
        if (window.innerWidth <= TOUCH_BREAKPOINT || !this.enableSticky) return
        this.progress = progress
    }

    onScroll() {
        if (this.isInview) this.play()
    }

    onUpdate() {
        this.smoothProgress += (this.progress - this.smoothProgress) * this.lerp
        this.smoothProgress = ((1000 * (this.smoothProgress + 0.001)) | 0) / 1000
        this.translateHorizontalCars(this.smoothProgress)

        // Add delay before stopping raf
        // ==============================================
        if (Math.round(this.smoothProgress) === Math.round(this.progress)) {
            this.rafIncrement++
            if (this.rafIncrement >= 50) {
                this.pause()
            }
        }
    }

    ///////////////
    // Methods
    ///////////////
    translateHorizontalCars(progress) {
        const translateValue = mapRange(0, 1, 0, -(this.containerWidth - window.innerWidth), progress)
        this.$grid.style.transform = `translate3d(${translateValue}px, 0, 0)`
    }

    computeStickyTop() {
        const topValue = window.innerHeight / 2  - this.$grid.offsetHeight / 2
        this.$sticky.style.setProperty("--sticky-top", `${topValue}px`);
    }

    computeContainerHeight() {
        this.containerWidth = this.$grid.offsetWidth

        if (this.containerWidth <= window.innerWidth) {
            this.enableSticky = false
            return this.reset()
        } else {
            this.enableSticky = true
        }

        this.containerHeight = Math.max(window.innerHeight + this.containerWidth - window.innerWidth, this.containerWidth - window.innerWidth)
        this.$container.style.height = `${this.containerHeight}px`
    }

    reset() {
        this.$grid.style.transform = ``
        this.$container.style.height = ``
        this.$sticky.style.setProperty("--sticky-top", `0`);
    }

    // RAF
    ///////////////
    play() {
        if (this.isRafPlaying) return

        this.isRafPlaying = true
        this.rafIncrement = 0

        gsap.ticker.add(this.onUpdateBind)
    }

    pause() {
        if (!this.isRafPlaying) return

        this.isRafPlaying = false

        gsap.ticker.remove(this.onUpdateBind)
    }

}
